import React from 'react';
import _ from 'lodash';

import { styled } from '@mui/material/styles';
import {
  Observable,
  Subject
} from 'rxjs';



const __defaultCollapsedWidthPx = 72;
const __defaultExpandedWidthPx = 265;
const __defaultLogoMarginLeftPx = 18;



export interface IStyleOptions {
  collapsedWidth?: number | string;
  expandedWidth?: number | string;
  backgroundColor?: string;
  borderWidth?: string;
  borderColor?: string;
  logoMarginLeft?: number | string;
}



interface IAppBarNavProps {
  isExpanded: boolean;
  styleOptions?: IStyleOptions;
}

const AppBarNav = styled('nav', {
  shouldForwardProp: prop =>
    prop !== 'isExpanded' &&
    prop !== 'styleOptions'
})<IAppBarNavProps>(({
  theme,
  isExpanded,
  styleOptions
}) => {
  const finalCollapsedWidth = styleOptions?.collapsedWidth ?? __defaultCollapsedWidthPx;
  const finalExpandedWidth = styleOptions?.expandedWidth ?? __defaultExpandedWidthPx;
  const finalBackgroundColor = styleOptions?.backgroundColor ?? theme.palette.secondary.main;
  const finalBorderWidth = styleOptions?.borderWidth ?? (
    styleOptions?.borderColor ? '1px' : undefined);
  const finalBorderColor = styleOptions?.borderColor;

  return {
    width: !isExpanded ?
      finalCollapsedWidth :
      finalExpandedWidth,
    transition: 'width 100ms ease-in-out',
    position: 'fixed',
    top: 0,
    bottom: 0,
    left: 0,
    zIndex: theme.zIndex.appBar,
    overflow: 'hidden',
    backgroundColor: finalBackgroundColor,
    borderRight: finalBorderColor ?
      `${finalBorderWidth} solid ${finalBorderColor}` :
      undefined,
    userSelect: 'none',
    boxShadow: isExpanded ?
      '0 0 10px 2px rgba(0, 0, 0, 0.1)' :
      undefined,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'stretch'
  };
});

interface IFullViewDivProps {
  expandedWidth?: number | string;
}

const FullViewDiv = styled('div', {
  shouldForwardProp: prop =>
    prop !== 'expandedWidth'
})<IFullViewDivProps>(({expandedWidth}) => ({
  width: expandedWidth ?? __defaultExpandedWidthPx,
  flex: '1 0',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  alignItems: 'stretch'
}));

interface ILogoDivProps {
  expandedWidth?: number | string;
  styleOptions?: IStyleOptions;
}

const LogoDiv = styled('div', {
  shouldForwardProp: prop =>
    prop !== 'expandedWidth' &&
    prop !== 'styleOptions'
})<ILogoDivProps>(({
  expandedWidth,
  styleOptions
}) => ({
  width: expandedWidth ?? __defaultExpandedWidthPx,
  marginLeft: styleOptions?.logoMarginLeft ?? __defaultLogoMarginLeftPx,
  marginBottom: 33
}));

const TopDiv = styled('div')(({theme}) => ({
  marginTop: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch'
}));

const BottomDiv = styled('div')(({theme}) => ({
  marginBottom: theme.spacing(1),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch'
}));



export interface IProps {
  elLogo: React.ReactNode;
  elMainContent: React.ReactNode;
  elFooterContent: React.ReactNode;
  onInit?: (obsIsExpanded: Observable<boolean>) => void;
  styleOptions?: IStyleOptions;
}

export const FC: React.FC<IProps> = props => {
  const [isExpanded, _setIsExpanded] = React.useState(false);

  const refSubjIsExpanded = React.useRef(new Subject<boolean>());



  React.useEffect(
    () => {
      if (props.onInit) {
        props.onInit(refSubjIsExpanded.current.asObservable());
      }
    },
    [props]
  );



  function setIsExpanded(value: boolean) {
    _setIsExpanded(value);
    refSubjIsExpanded.current.next(value);
  }



  return (
    <AppBarNav
      isExpanded={isExpanded}
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
      styleOptions={props.styleOptions}>

      <FullViewDiv
        expandedWidth={props.styleOptions?.expandedWidth}>
        <TopDiv>
          <LogoDiv
            expandedWidth={props.styleOptions?.expandedWidth}
            styleOptions={props.styleOptions}>
            {props.elLogo}
          </LogoDiv>

          {props.elMainContent}
        </TopDiv>

        <BottomDiv>
          {props.elFooterContent}
        </BottomDiv>
      </FullViewDiv>

    </AppBarNav>
  );
};
